import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { ArrowRight, Leaf, Mail, MessageCircle, Share2, ShieldCheck, Smartphone } from 'lucide-react';
import { useAuth } from '@/auth/AuthProvider';
import {
  BLOCKED_MESSAGES,
  LOGIN_CHANNELS,
  maskIdentifier,
  normaliseIdentifier,
  type LoginChannel,
} from '@/auth/session';
import { getSupabase } from '@/lib/supabase';
import { VanamLogo } from '@/components/VanamLogo';

/**
 * Sign-in by one-time code over WhatsApp, SMS or email. There is no password and no sign-up:
 * Supabase Auth has sign-up disabled on every channel (supabase/config.toml) and the request
 * is made with `shouldCreateUser: false`, so a code is only ever sent to a login an
 * organisation admin's invitation has already produced.
 *
 * The request step always answers the same way, whether or not the contact is known. Saying
 * "no such user" would turn the login form into a way to find out who works here.
 */

const RESEND_SECONDS = 30;
const CHANNEL_ICONS = { whatsapp: MessageCircle, sms: Smartphone, email: Mail } as const;

type Step = { kind: 'identify' } | { kind: 'code'; channel: LoginChannel; identifier: string };

// Local-only test sign-in. `import.meta.env.DEV` is false in a production build, so the module is
// never bundled there.

export default function LoginScreen() {
  const { status, blockedReason } = useAuth();
  const location = useLocation();
  const [channel, setChannel] = useState<LoginChannel>('whatsapp');
  const [identifier, setIdentifier] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<Step>({ kind: 'identify' });
  const [error, setError] = useState<string>();
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const codeInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (resendIn <= 0) return;
    const timer = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendIn]);

  useEffect(() => {
    if (step.kind === 'code') codeInput.current?.focus();
  }, [step.kind]);

  if (status === 'active') {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate replace to={from && from !== '/login' ? from : '/'} />;
  }

  async function sendCode(target: LoginChannel, value: string) {
    const supabase = getSupabase();
    // Auth does not tell the SMS hook which channel was chosen, so the page says so first
    // (migration 0069). If this fails the code still goes - WhatsApp first, SMS as fallback.
    if (target !== 'email') {
      try {
        const pub = supabase.schema('pub') as unknown as { rpc: (fn: string, args: Record<string, string>) => PromiseLike<unknown> };
        await pub.rpc('fn_request_login_channel', { p_phone: value, p_channel: target });
      } catch {
        // Not fatal: without it the default order applies.
      }
    }
    const { error: sendError } = target === 'email'
      ? await (async () => {
          // Who, from where: printed in the sign-in email (owner, 2026-09-22). Never blocks sign-in.
          await supabase.functions.invoke('prepare-sign-in', { body: { email: value } }).catch(() => undefined);
          return supabase.auth.signInWithOtp({ email: value, options: { shouldCreateUser: false } });
        })()
      : await supabase.auth.signInWithOtp({ phone: value, options: { shouldCreateUser: false, channel: target } });

    // An unknown contact comes back as "Signups not allowed for otp", and NO code is sent. This
    // used to be swallowed so the screen would not reveal which numbers are registered - but it
    // then showed "enter your code" for a code that was never coming (owner, 2026-09-21). For an
    // invite-only staff app, saying so plainly wins; sends are still rate limited server-side.
    if (sendError && /signups? not allowed|user not found/i.test(sendError.message)) {
      throw new Error(target === 'email'
        ? 'This email is not registered for Vanam.S360.earth. Ask your organisation admin to invite you, or use the number or email they registered.'
        : 'This number is not registered for Vanam.S360.earth. Ask your organisation admin to invite you, or use the number or email they registered.');
    }
    if (sendError) {
      throw new Error(/rate limit|security purposes/i.test(sendError.message)
        ? 'Too many requests. Wait a minute and try again.'
        : target === 'sms'
          ? 'The SMS could not be sent. Try WhatsApp, or try again shortly.'
          : 'The code could not be sent. Try another channel, or try again shortly.');
    }
  }

  async function handleIdentify(event: FormEvent) {
    event.preventDefault();
    setError(undefined);
    setBannerDismissed(true);          // a new attempt: an earlier attempt's refusal no longer applies
    const normalised = normaliseIdentifier(channel, identifier);
    if ('error' in normalised) {
      setError(normalised.error);
      return;
    }
    setBusy(true);
    try {
      await sendCode(channel, normalised.value);
      setCode('');
      setStep({ kind: 'code', channel, identifier: normalised.value });
      setResendIn(RESEND_SECONDS);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The code could not be sent.');
    } finally {
      setBusy(false);
    }
  }

  async function handleVerify(event: FormEvent) {
    event.preventDefault();
    if (step.kind !== 'code') return;
    setError(undefined);
    if (!/^\d{6}$/.test(code)) {
      setError('Enter the 6-digit code.');
      return;
    }
    setBusy(true);
    const supabase = getSupabase();
    const { error: verifyError } = step.channel === 'email'
      ? await supabase.auth.verifyOtp({ email: step.identifier, token: code, type: 'email' })
      : await supabase.auth.verifyOtp({ phone: step.identifier, token: code, type: 'sms' });
    setBusy(false);
    if (verifyError) {
      setError('That code is not right, or it has expired. Check it, or send a new one.');
      return;
    }
    // Success: AuthProvider sees SIGNED_IN, loads the profile, and this screen redirects -
    // or, for a login the application does not approve, shows the blocked message.
  }

  async function handleResend() {
    if (step.kind !== 'code' || resendIn > 0) return;
    setError(undefined);
    setBusy(true);
    try {
      await sendCode(step.channel, step.identifier);
      setResendIn(RESEND_SECONDS);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The code could not be sent.');
    } finally {
      setBusy(false);
    }
  }

  const activeChannel = LOGIN_CHANNELS.find((entry) => entry.key === channel)!;

  return (
    <div className="login-screen">
      <div className="login-layout">
      <div className="login-card">
        <div className="login-brand">
          <VanamLogo height={96} variant="full" />
          <span>FSC and EUDR Project Management</span>
        </div>

        {status === 'blocked' && blockedReason && !bannerDismissed && (
          <p className="login-alert" role="alert">
            <span><strong>Access restricted</strong>{BLOCKED_MESSAGES[blockedReason]}</span>
          </p>
        )}

        {step.kind === 'identify' ? (
          <form onSubmit={handleIdentify} noValidate>
            <h1>Sign in</h1>
            <p className="login-lead">Choose where to receive your one-time code.</p>

            <div className="login-channels" role="radiogroup" aria-label="Where to send the code">
              {LOGIN_CHANNELS.map((entry) => {
                const Icon = CHANNEL_ICONS[entry.key];
                return (
                  <button
                    aria-checked={channel === entry.key}
                    className={channel === entry.key ? 'active' : ''}
                    key={entry.key}
                    onClick={() => { setChannel(entry.key); setError(undefined); }}
                    role="radio"
                    type="button"
                  >
                    <Icon size={18} /> {entry.label}
                  </button>
                );
              })}
            </div>

            <label className="login-field">
              <span>{channel === 'email' ? 'Email address' : channel === 'whatsapp' ? 'WhatsApp number' : 'Mobile number'}</span>
              <input
                aria-invalid={Boolean(error)}
                autoComplete={channel === 'email' ? 'email' : 'tel'}
                autoFocus
                inputMode={channel === 'email' ? 'email' : 'tel'}
                onChange={(event) => setIdentifier(event.target.value)}
                placeholder={channel === 'email' ? 'name@organisation.org' : '98xxxxxx01'}
                type={channel === 'email' ? 'email' : 'tel'}
                value={identifier}
              />
              <small>{activeChannel.hint}</small>
            </label>

            {error && <p className="login-error" role="alert">{error}</p>}

            <button className="login-submit" disabled={busy || status === 'loading'} type="submit">
              {busy ? 'Sending...' : <>Send code <ArrowRight size={18} /></>}
            </button>
            <p className="login-foot">Only users invited by an organisation admin can sign in. There is no self sign-up.</p>
          </form>
        ) : (
          <form onSubmit={handleVerify} noValidate>
            <h1>Enter your code</h1>
            <p className="login-lead">
              A 6-digit code has been sent to <strong>{maskIdentifier(step.channel, step.identifier)}</strong> by{' '}
              {LOGIN_CHANNELS.find((entry) => entry.key === step.channel)!.label}.
            </p>

            <label className="login-field">
              <span>One-time code</span>
              <input
                aria-invalid={Boolean(error)}
                autoComplete="one-time-code"
                className="login-code"
                inputMode="numeric"
                maxLength={6}
                onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                ref={codeInput}
                value={code}
              />
            </label>

            {error && <p className="login-error" role="alert">{error}</p>}

            <button className="login-submit" disabled={busy || code.length !== 6} type="submit">
              {busy ? 'Checking...' : 'Verify and sign in'}
            </button>

            <div className="login-secondary">
              <button disabled={busy || resendIn > 0} onClick={handleResend} type="button">
                {resendIn > 0 ? `Resend in ${resendIn}s` : 'Resend code'}
              </button>
              <button onClick={() => { setStep({ kind: 'identify' }); setError(undefined); }} type="button">
                Use a different number or channel
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Beside the card on a wide screen (2026-09-27 design boards); the stylesheet hides it
          below 1100px. Decorative: the card already says what the app is. */}
      <aside aria-hidden="true" className="login-aside">
        <span className="login-aside-tag">Staff portal</span>
        <div>
          <h2>Every tree, plot and farmer on one record.</h2>
          <p>Registers, geofencing, consent and evidence packs for FSC and EUDR, kept by your organisation.</p>
        </div>
        <div className="login-points">
          <div><ShieldCheck size={30} strokeWidth={1.6} /><span>Trusted<br />Compliance</span></div>
          <div><Leaf size={30} strokeWidth={1.6} /><span>Sustainable<br />Supply Chains</span></div>
          <div><Share2 size={30} strokeWidth={1.6} /><span>Technology<br />for Impact</span></div>
        </div>
      </aside>
      </div>
    </div>
  );
}
